/**
 * @format
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Avatar,
  Stack,
  Tooltip,
  Paper,
  Switch,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import CheckCircle from '@mui/icons-material/CheckCircle';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import UserDetails from './UserDetails';
import UserEditForm from './UserEditForm';
import DynamicSearchBar from '../CreateNewUser/components/DynamicSearchbar';
import {
  getUserRoleMappings,
  updateUserProfile,
  moveUserFromProductionToStage,
} from '../../../core/services/keycloakApi';
import { notifyAdminOnStatus } from '../../../core/services/profile.service';

const isStagingEnv =
  process.env.REACT_APP_ENV === 'staging' ||
  process.env.REACT_APP_ENV === 'development';

const actionButtonSx = {
  color: 'white',
  borderColor: 'rgba(255,255,255,0.3)',
  fontSize: 11,
  px: 1.2,
};

const UserList = ({
  users,
  selectedUser,
  isEditing,
  setSelectedUser,
  setIsEditing,
  setFormData,
  formData,
  fetchUsers,
  onFormSuccess,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [userRoles, setUserRoles] = useState([]);
  const [statusLoadingId, setStatusLoadingId] = useState(null);
  const [moveLoadingId, setMoveLoadingId] = useState(null);
  const [moveConfirmUser, setMoveConfirmUser] = useState(null);
  const [optimisticEnabled, setOptimisticEnabled] = useState({});

  const handleViewDetails = (user) => {
    setSelectedUser(user);
    setIsEditing(false);
  };

  const handleEditClick = (user) => {
    setSelectedUser(user);
    setIsEditing(true);

    const [firstName, ...rest] = user.name.split(' ');
    setFormData({
      username: user.uname,
      firstName: firstName || '',
      lastName: rest.join(' ') || '',
      email: user.email,
      upass: '',
      role_id: user.role_id || '',
    });
  };

  const filteredUsers = users.filter((user) => {
    const term = searchTerm.toLowerCase().trim();
    if (!term) return true;

    return (
      user.name.toLowerCase().includes(term) ||
      user.uname.toLowerCase().includes(term) ||
      user.email.toLowerCase().includes(term)
    );
  });

  const handleUserStatus = async function (user) {
    if (statusLoadingId) return;

    const nextEnabled = !user.enabled;
    setStatusLoadingId(user.uid);
    setOptimisticEnabled((prev) => ({ ...prev, [user.uid]: nextEnabled }));

    try {
      await updateUserProfile(user.uid, { enabled: nextEnabled });
      const formData = new FormData();
      formData.append('email', user.email);
      formData.append('firstName', user.firstName);
      formData.append('lastName', user.lastName);
      formData.append('enable', nextEnabled); // Note: FormData stringifies booleans as "true"/"false"
      await notifyAdminOnStatus(formData);
      onFormSuccess?.('User status updated successfully', 'success');
      await fetchUsers();
    } catch (error) {
      setOptimisticEnabled((prev) => ({ ...prev, [user.uid]: user.enabled }));
      onFormSuccess?.('Failed to update user status', 'error');
    } finally {
      setStatusLoadingId(null);
      setOptimisticEnabled((prev) => {
        const next = { ...prev };
        delete next[user.uid];
        return next;
      });
    }
  };

  const handleOpenMoveConfirm = (user) => {
    if (moveLoadingId) return;
    setMoveConfirmUser(user);
  };

  const handleCloseMoveConfirm = () => {
    if (moveLoadingId) return;
    setMoveConfirmUser(null);
  };

  const handleConfirmMoveToStage = async () => {
    if (!moveConfirmUser || moveLoadingId) return;

    const user = moveConfirmUser;
    setMoveLoadingId(user.uid);
    try {
      await moveUserFromProductionToStage(user.uid);
      onFormSuccess?.(
        `${user.name || user.uname} moved to stage successfully`,
        'success'
      );
      setMoveConfirmUser(null);
      await fetchUsers();
    } catch {
      onFormSuccess?.('Failed to move user to stage', 'error');
    } finally {
      setMoveLoadingId(null);
    }
  };

  useEffect(() => {
    if (selectedUser && !isEditing) {
      const fetchRoles = async () => {
        try {
          const res = await getUserRoleMappings(selectedUser.uid);
          const clientMappings = res?.data?.data?.clientMappings;
          const mappings = clientMappings
            ? Object.values(clientMappings).flatMap((c) => c.mappings || [])
            : [];
          setUserRoles(mappings);
        } catch {
          onFormSuccess?.('Failed to fetch user roles', 'error');
        }
      };
      fetchRoles();
    }
  }, [selectedUser, isEditing, onFormSuccess]);

  return (
    <Box sx={{ p: 1.5 }}>
      <DynamicSearchBar
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        placeholder="Search users..."
      />

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
        }}
      >
        <Box
          sx={{
            flex: 1,
            maxHeight: { xs: 'none', md: '56vh' },
            overflowY: { xs: 'visible', md: 'auto' },
            pr: { xs: 0, md: 1 },

            '&::-webkit-scrollbar': {
              width: '5px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(255,255,255,0.3)',
              borderRadius: '10px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: 'rgba(255,255,255,0.5)',
            },

            scrollbarWidth: 'thin',
            scrollbarColor: 'rgba(255,255,255,0.3) transparent',
          }}
        >
          {filteredUsers.map((user) => {
            const isSelected = selectedUser?.uid === user.uid;
            const isStatusLoading = statusLoadingId === user.uid;
            const isMoveLoading = moveLoadingId === user.uid;
            const isEnabled =
              optimisticEnabled[user.uid] !== undefined
                ? optimisticEnabled[user.uid]
                : user.enabled;

            return (
              <Card
                key={user.uid}
                sx={{
                  mb: 1,
                  borderRadius: 2,
                  border: isSelected
                    ? '1px solid rgba(255,255,255,0.35)'
                    : '1px solid rgba(255,255,255,0.15)',
                  backdropFilter: 'blur(6px)',
                  background: isSelected
                    ? 'rgba(255,255,255,0.08)'
                    : 'rgba(255,255,255,0.05)',
                  color: 'white',
                }}
              >
                <CardContent sx={{ py: 1, px: 1.5 }}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar
                      src={user.profileImageUrl || undefined}
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.15)',
                        width: 30,
                        height: 30,
                      }}
                    >
                      {isSelected ? (
                        <CheckCircle sx={{ fontSize: 18 }} />
                      ) : (
                        <PersonIcon sx={{ fontSize: 18 }} />
                      )}
                    </Avatar>

                    <Box sx={{ minWidth: 0 }}>
                      <Typography fontWeight={600} fontSize={13}>
                        {user.name}
                      </Typography>

                      <Typography
                        fontSize={12}
                        sx={{ color: 'rgba(255,255,255,0.6)' }}
                      >
                        @{user.uname}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>

                <CardActions
                  sx={{ justifyContent: 'flex-end', py: 1, px: 1.5 }}
                >
                  <Tooltip title="View">
                    <Button
                      size="small"
                      startIcon={<VisibilityIcon sx={{ fontSize: 16 }} />}
                      onClick={() => handleViewDetails(user)}
                      sx={actionButtonSx}
                      variant={
                        isSelected && !isEditing ? 'contained' : 'outlined'
                      }
                    >
                      View
                    </Button>
                  </Tooltip>

                  <Tooltip title="Edit">
                    <Button
                      size="small"
                      startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                      onClick={() => handleEditClick(user)}
                      sx={actionButtonSx}
                      variant={
                        isSelected && isEditing ? 'contained' : 'outlined'
                      }
                    >
                      Edit
                    </Button>
                  </Tooltip>

                  {isStagingEnv && (
                    <Tooltip title="Move from production to stage">
                      <span>
                        <Button
                          size="small"
                          startIcon={
                            isMoveLoading ? (
                              <CircularProgress
                                size={12}
                                thickness={5}
                                sx={{ color: 'rgba(255,255,255,0.7)' }}
                              />
                            ) : (
                              <SwapHorizIcon sx={{ fontSize: 16 }} />
                            )
                          }
                          onClick={() => handleOpenMoveConfirm(user)}
                          disabled={!!moveLoadingId}
                          sx={actionButtonSx}
                          variant="outlined"
                        >
                          {isMoveLoading ? 'Moving...' : 'To Stage'}
                        </Button>
                      </span>
                    </Tooltip>
                  )}

                  {/* status */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Switch
                      checked={isEnabled}
                      onChange={() => handleUserStatus(user)}
                      disabled={!!statusLoadingId}
                      size="small"
                      inputProps={{ 'aria-label': 'Status' }}
                      sx={{
                        width: 36,
                        height: 22,
                        p: 0,
                        opacity: isStatusLoading ? 0.7 : 1,
                        transition: 'opacity 0.2s ease',
                        '& .MuiSwitch-switchBase': {
                          p: 0.35,
                          transition:
                            'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&.Mui-checked': {
                            transform: 'translateX(14px)',
                            color: '#111',
                            '& + .MuiSwitch-track': {
                              bgcolor: '#4ADE80',
                              opacity: 1,
                            },
                          },
                        },
                        '& .MuiSwitch-thumb': {
                          width: 16,
                          height: 16,
                          transition: 'box-shadow 0.2s ease',
                        },
                        '& .MuiSwitch-track': {
                          borderRadius: 11,
                          bgcolor: 'rgba(255,255,255,0.2)',
                          opacity: 1,
                          transition: 'background-color 0.25s ease',
                        },
                      }}
                    />
                    {isStatusLoading && (
                      <CircularProgress
                        size={12}
                        thickness={5}
                        sx={{ color: 'rgba(255,255,255,0.7)' }}
                      />
                    )}
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: isEnabled ? '#5EEAD4' : 'rgba(255,255,255,0.5)',
                        transition: 'color 0.25s ease',
                      }}
                    >
                      {isStatusLoading
                        ? 'Updating...'
                        : isEnabled
                          ? 'Active'
                          : 'Inactive'}
                    </Typography>
                  </Box>
                </CardActions>
              </Card>
            );
          })}
        </Box>

        <Box sx={{ flex: 1 }}>
          {selectedUser ? (
            <Paper
              sx={{
                borderRadius: 2,
                background: 'rgba(255,255,255,0.05)',
                backdropFilter: 'blur(6px)',
                border: '1px solid rgba(255,255,255,0.15)',
              }}
            >
              {isEditing ? (
                <UserEditForm
                  userId={selectedUser.uid}
                  formData={formData}
                  setFormData={setFormData}
                  onSuccess={onFormSuccess}
                />
              ) : (
                <UserDetails user={selectedUser} userRoles={userRoles} />
              )}
            </Paper>
          ) : (
            <Typography
              align="center"
              fontSize={13}
              sx={{ color: 'rgba(255,255,255,0.6)' }}
            >
              Select a user to view details
            </Typography>
          )}
        </Box>
      </Box>

      <Dialog
        open={Boolean(moveConfirmUser)}
        onClose={handleCloseMoveConfirm}
        maxWidth="xs"
        fullWidth
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0,0,0,0.45)',
            backdropFilter: 'blur(2px)',
          },
        }}
        PaperProps={{
          sx: {
            background: 'rgba(255,255,255,0.05)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: 2,
            color: '#fff',
          },
        }}
      >
        <DialogTitle sx={{ fontSize: 16 }}>Move to Stage</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography fontSize={13} sx={{ color: 'rgba(255,255,255,0.85)' }}>
            Are you sure you want to move{' '}
            <Box component="span" sx={{ fontWeight: 600, color: '#fff' }}>
              {moveConfirmUser?.name || moveConfirmUser?.uname}
            </Box>{' '}
            @{moveConfirmUser?.uname} from production to stage?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button
            onClick={handleCloseMoveConfirm}
            variant="outlined"
            disabled={!!moveLoadingId}
            sx={{
              color: '#fff',
              fontSize: 13,
              borderColor: 'rgba(255,255,255,0.3)',
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmMoveToStage}
            disabled={!!moveLoadingId}
            startIcon={
              moveLoadingId ? (
                <CircularProgress size={14} thickness={5} sx={{ color: '#fff' }} />
              ) : null
            }
            sx={{
              fontSize: 13,
              background: 'rgba(255,255,255,0.18)',
              color: '#fff',
              boxShadow: 'none',
              '&:hover': {
                background: 'rgba(255,255,255,0.28)',
              },
              '&.Mui-disabled': {
                color: 'rgba(255,255,255,0.5)',
                background: 'rgba(255,255,255,0.1)',
              },
            }}
          >
            {moveLoadingId ? 'Moving...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserList;
